/**
 * CustomFieldInput Component
 *
 * Dynamic input component that renders the appropriate form field based on field type.
 * Supports text, textarea, number, select, multiselect, date, boolean, and currency.
 *
 * DDD Compliance:
 * - Purely presentational
 * - Receives field definition and value from parent
 * - Emits changes through onChange callback
 */

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { CustomFieldDefinitionDto } from '@/types/job-type';

export interface CustomFieldInputProps {
  definition: CustomFieldDefinitionDto;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
}

export function CustomFieldInput({
  definition,
  value,
  onChange,
  error,
  disabled,
}: CustomFieldInputProps) {
  const fieldId = `field-${definition.name}`;

  const renderInput = () => {
    switch (definition.fieldType) {
      case 'text':
        return (
          <Input
            id={fieldId}
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={definition.helpText}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'number':
        return (
          <Input
            id={fieldId}
            type="number"
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => {
              const numValue = e.target.value === '' ? undefined : parseFloat(e.target.value);
              onChange(numValue);
            }}
            placeholder={definition.helpText}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
            step="any"
          />
        );

      case 'select':
        return (
          <Select
            id={fieldId}
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          >
            <option value="">Select {definition.label}</option>
            {definition.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2 border border-neutral-300 rounded-md p-3">
            {definition.options?.map((option) => {
              const selectedValues = (value as string[]) || [];
              const isChecked = selectedValues.includes(option);

              return (
                <div key={option} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`${fieldId}-${option}`}
                    checked={isChecked}
                    onChange={(e) => {
                      const currentValues = (value as string[]) || [];
                      const newValues = e.target.checked
                        ? [...currentValues, option]
                        : currentValues.filter((v) => v !== option);
                      onChange(newValues);
                    }}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <label
                    htmlFor={`${fieldId}-${option}`}
                    className="text-sm text-neutral-700"
                  >
                    {option}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case 'date':
        return (
          <Input
            id={fieldId}
            type="date"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={fieldId}
              checked={(value as boolean) || false}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 rounded border-neutral-300 text-primary-500 focus:ring-2 focus:ring-primary-500"
            />
            <Label htmlFor={fieldId}>{definition.helpText || definition.label}</Label>
          </div>
        );

      default:
        return <div className="text-sm text-neutral-500">Unsupported field type</div>;
    }
  };

  return (
    <div className="space-y-2">
      {definition.fieldType !== 'boolean' && (
        <Label htmlFor={fieldId}>
          {definition.label}
          {definition.isRequired && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      {definition.helpText && definition.fieldType !== 'boolean' && (
        <p className="text-xs text-neutral-500">{definition.helpText}</p>
      )}
      {renderInput()}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
